// transactionWorkflowContext — the workflow context object + accessor hooks.
//
// Split out of `useTransactionWorkflow.tsx` so that module only exports
// components (react-refresh/only-export-components). See that file's header
// for what the provider hoists and why.

import { createContext, useContext } from "react";

import type { PricingSide } from "@shared/types";
import type {
  PipelineActions,
  PipelineActionsResult,
  PipelineExternalHooks,
} from "./usePipelineActions";

export interface TransactionWorkflowContextValue {
  /** Pipeline mutations (save weight/bales, uploads, generate docs, etc). */
  actions: PipelineActions;
  /** External modal triggers — shared modals owned by the provider. */
  external: PipelineExternalHooks;
  /** Bulk action factory — filters by allowed keys per preset. */
  bulkActions: PipelineActionsResult["bulkActions"];
  /**
   * Multi-transaction invoice generation — used by admin queue's
   * revenue-groups tab where one row represents many transactions for
   * the same buyer/month and the user issues one invoice for the whole
   * group. Single-tx generation lives on `actions.generateLoadInvoice`
   * and `actions.generateUnloadInvoice`.
   */
  generateInvoiceForTransactions: (
    txIds: number[],
    side: PricingSide,
    groupKey: string,
  ) => void;
  /** The currently-preparing groupKey, or null. */
  preparingInvoiceGroupKey: string | null;
  /**
   * Register a refetch callback (e.g. the list DataTable or a view's
   * useOne refetch). Returns an unsubscribe function.
   */
  registerRefetch: (fn: () => void) => () => void;
  /** Call all registered refetches. Used by mutations and provider modals. */
  refetchAll: () => void;
  /** Same but for file sub-queries (photos/docs pills). */
  refetchAllFiles: () => void;
}

export const TransactionWorkflowContext =
  createContext<TransactionWorkflowContextValue | null>(null);

export const useTransactionWorkflow = (): TransactionWorkflowContextValue => {
  const ctx = useContext(TransactionWorkflowContext);
  if (!ctx) {
    throw new Error(
      "useTransactionWorkflow must be used inside <TransactionWorkflowProvider>",
    );
  }
  return ctx;
};

export const useTransactionWorkflowOptional =
  (): TransactionWorkflowContextValue | null =>
    useContext(TransactionWorkflowContext);
